import { useTenantApi } from '@/account/hook/useTenantApi';
import { TicketType } from '@/api/constant';
import {
  cartQueryKey,
  getProductType,
  saveCartItemWithTempRegistrations,
  deleteCartItem,
  type CartItem,
  type TicketData,
  type CartItemDisplay,
  type TempRegistration,
  type CartItemSaveWithTempRegistrationsRequest,
  type CartItemDeleteRequest,
  type CartItemUpdateRequest,
} from '@/api/registration';
import { groupRegistrationQueryKey } from '@/api/registration/groupRegistration';
import BaseFormCheckboxListField, { type CheckboxOption } from '@/base/Form/CheckboxListField';
import BaseFormFieldGroup from '@/base/Form/FieldGroup';
import {
  UiButton,
  UiDrawer,
  UiDrawerBody,
  UiDrawerCloseButton,
  UiDrawerContent,
  UiDrawerOverlay,
  UiHStack,
  UiIconCurrencyCircleDollar,
  UiIconStackSimple,
  UiStack,
  UiText,
  uiStyles,
  type UiDrawerProps, UiIconShoppingCart, UiDrawerFooter, UiDrawerHeader, UiIconPlusCircle,
} from '@/lib/ui';
import { useGroupCartQuery } from '@/registration/hook/useGroupCartQuery';
import { useGroupInStockItem } from '@/registration/hook/useGroupInStockItem';
import { useGroupRegistrationQuery } from '@/registration/hook/useGroupRegistrationQuery';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Form, Formik, type FormikHelpers } from 'formik';
import { useCallback, useMemo, type FC, useState, useEffect } from 'react';
import BaseDividerHorizontal from '@/base/Divider/Horizontal';
import BaseMessageBarInfo from '@/base/MessageBar/Info';
import { useEventSettingsQuery } from '@/registration/hook/useEventSettingsQuery';
import BaseButtonIconText from '@/base/Button/IconText';
import { useShoppingCartToggle } from '@/registration/component/Register/ShoppingCart/ShoppingCartToggleProvider';
import { type CartTempRegistration, useGroupShoppingCart } from '@/registration/hook/useGroupShoppingCart';
import TicketRegistrations from '@/registration/register/GroupMainTickets/TicketRegistrations';
import { uniqueObjectList } from '@/lib/util';
import BaseThemeButton from '@/base/ThemeButton';

export interface TicketDrawerProps extends Omit<UiDrawerProps, 'children'> {
  ticket: TicketData
  isOpen: boolean
  onClose: () => void
  sharedTicketIds: number[]
  displayCapacities?: boolean
}

export interface FormData {
  tempRegistrationIds: string[]
}

const initFormData: FormData = {
  tempRegistrationIds: [],
};

const TicketDrawer: FC<TicketDrawerProps> = ({
  ticket,
  isOpen,
  onClose,
  sharedTicketIds,
  displayCapacities = true,
  ...props
}) => {
  const queryClient = useQueryClient();
  const { eventId } = useRegisterRoute();
  const groupRegistrationQuery = useGroupRegistrationQuery(eventId);
  const groupRegistration = groupRegistrationQuery?.data;
  const { data: cart } = useGroupCartQuery(eventId);
  const { totalSharedStockInCart, tempRegistrationIds } = useGroupInStockItem(ticket, sharedTicketIds);
  const { createTenantApiRequest } = useTenantApi();
  const { data: eventSetting } = useEventSettingsQuery(eventId);
  const { onOpen: onShoppingCartOpen } = useShoppingCartToggle();
  const [ addedRegistrations, setAddedRegistrations ] = useState<TempRegistration[]>([]);
  const [ availableRegistrations, setAvailableRegistrations ] = useState<TempRegistration[]>([]);
  const { cartTempRegistrationList, getTicketCartItemId, isLoading: isGroupShoppingCartLoading, getTicketTempRegistrationList } = useGroupShoppingCart(eventId);

  // Get the attendee registrations that have been added, and the ones that haven't been added to any ticket.
  useEffect(() => {
    if (!isGroupShoppingCartLoading && groupRegistration) {
      const _addedRegistrations: CartTempRegistration[]  = getTicketTempRegistrationList(ticket);
      const _availableRegistrations: TempRegistration[] = [];
      const _mainTicketTempRegoIds: number[] = [];

      // console.log('--------', cartTempRegistrationList);

      // Get the temp registrations that have already been added into the current ticket.
      for (const tempRegoCartInfo of cartTempRegistrationList) {
        if (tempRegoCartInfo.registrationTypeId) {
          _mainTicketTempRegoIds.push(tempRegoCartInfo.id);
        }
      }

      // Get the available temp registration for the current ticket.
      if (groupRegistration.tempRegistrations && Array.isArray(groupRegistration.tempRegistrations)) {
        if (ticket.type === TicketType.Main) {
          // console.log('111', _mainTicketTempRegoIds, groupRegistration.tempRegistrations, ticket);
          // For main tickets, the available temp registration only include the ones that haven't been added into any main tickets.
          for (const rego of groupRegistration.tempRegistrations) {
            if (!_mainTicketTempRegoIds.includes(rego.id)) {
              _availableRegistrations.push(rego);
            }
          }
        } else {
          // For non-main tickets, the available temp registrations include the ones that haven't been added into the current ticket.
          for (const rego of groupRegistration.tempRegistrations) {
            if (_addedRegistrations.findIndex((addedRego) => {return addedRego.id === rego.id; }) === -1) {
              _availableRegistrations.push(rego);
            }
          }
        }
      }
      // setAddedRegistrations(_addedRegistrations);
      // setAvailableRegistrations(_availableRegistrations);
      setAddedRegistrations(uniqueObjectList(_addedRegistrations, (currentItem, list) => {
        return list.findIndex((item: TempRegistration) => item.id === currentItem.id);
      }));
      setAvailableRegistrations(uniqueObjectList(_availableRegistrations, (currentItem, list) => {
        return list.findIndex((item: TempRegistration) => item.id === currentItem.id);
      }));
    }
  }, [
    cartTempRegistrationList,
    isGroupShoppingCartLoading,
    groupRegistration,
  ]);

  const onAddRegistration = useCallback((registration: TempRegistration) => {
    if (registration?.id) {
      // Add it to the added regos.
      setAddedRegistrations([...addedRegistrations, registration]);
      const addedRegisAmount = addedRegistrations.length + 1;

      // Remove it from the available regos.
      const _newAvailableRegistrations = [...availableRegistrations];
      const _toRemoveRegistrationIndex = _newAvailableRegistrations.findIndex((rego) => { return rego.id === registration.id });
      if (_toRemoveRegistrationIndex !== -1) {
        _newAvailableRegistrations.splice(_toRemoveRegistrationIndex, 1);
        const isOutStock = (ticket.currentStock - totalSharedStockInCart) - addedRegisAmount <= 0;
        if (isOutStock) {
          const disabledAvailableRegistrations = _newAvailableRegistrations.map((reg) => {
            return {
              ...reg,
              disabled: true,
            };
          });
          setAvailableRegistrations(disabledAvailableRegistrations);
          return;
        }
        setAvailableRegistrations(_newAvailableRegistrations);
      }
    }
  }, [
    addedRegistrations,
    setAddedRegistrations,
    availableRegistrations,
    setAvailableRegistrations,
  ]);

  const onRemoveRegistration = useCallback((registration: TempRegistration) => {
    if (registration?.id) {
      // Remove it from the added regos.
      const _newAddedRegistrations = [...addedRegistrations];
      const _toAddRegistrationIndex = _newAddedRegistrations.findIndex((rego) => {return rego.id === registration.id});
      if (_toAddRegistrationIndex !== -1) {
        _newAddedRegistrations.splice(_toAddRegistrationIndex, 1);
        setAddedRegistrations(_newAddedRegistrations);
      }

      const _newAvailableRegistrations = availableRegistrations.map((reg) => {
        return {
          ...reg,
          disabled: false,
        };
      });
      // Add it to the available regos.
      setAvailableRegistrations([..._newAvailableRegistrations, registration]);
    }
  }, [
    addedRegistrations,
    setAddedRegistrations,
    availableRegistrations,
    setAvailableRegistrations,
  ]);

  // const options: CheckboxOption[] = useMemo(() => {
  //   const existingCartItem = cart?.cartItems?.filter((item: CartItem) => {
  //     return item.productId !== ticket.id && item.productType === 'DelegateType' && ticket.type === TicketType.Main;
  //   });
  //   return groupRegistration?.tempRegistrations?.map((tempRegistration: TempRegistration) => {
  //     const isDisabled = existingCartItem?.some(e => {
  //       return e.tempRegistrationIds?.includes(tempRegistration.id);
  //     });
  //
  //     return {
  //       value: tempRegistration.id.toString(),
  //       label: `${tempRegistration.firstName} ${tempRegistration.lastName}`,
  //       disabled: isDisabled,
  //     };
  //   }) ?? [];
  // }, [cart?.cartItems, groupRegistration?.tempRegistrations, ticket.id, ticket.type]);

  // Update the current ticket in the shopping cart.
  const { mutate, isLoading } = useMutation<CartItem, Error, CartItemSaveWithTempRegistrationsRequest>(
    async (params: CartItemSaveWithTempRegistrationsRequest) => {
      const response = await saveCartItemWithTempRegistrations(createTenantApiRequest)(params);
      return response.items?.[0];
    },
    {
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [cartQueryKey, { groupRegistrationId: groupRegistration?.id }] }),
          queryClient.invalidateQueries({ queryKey: [groupRegistrationQueryKey, { eventId }] }),
        ]);
        onClose();
      }
    }
  );

  // Delete the current ticket from the shopping cart.
  // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
  const { mutate: deleteMutate, isLoading: deleteMutateIsLoading } = useMutation<void, Error, CartItemDeleteRequest>({
    mutationFn: async (request: CartItemDeleteRequest) => {
      await deleteCartItem(createTenantApiRequest)(request);
    },
    onSuccess: async (result) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [cartQueryKey, { groupRegistrationId: groupRegistration?.id }] }),
        queryClient.invalidateQueries({ queryKey: [groupRegistrationQueryKey, { eventId }] }),
      ]);
    },
    onError: () => {
      // NOTE: handle error
    }
  });

  // const mainTicketsInCart = useMemo(() => {
  //   return cart?.cartItems?.filter((item: CartItemDisplay) => {
  //     return item.ticketType === ticket.type && item.ticketType === TicketType.Main;
  //   }).flatMap((item) => {
  //     return item.tempRegistrationIds ?? [];
  //   }).map((id) => { return id.toString(); }) ?? []
  // }, [cart]);

  // const existingTempRegistrationIds = useCallback(() => {
  //   if (!groupRegistration) {
  //     return [];
  //   }
  //   const existingCartItem = cart?.cartItems?.find((item: CartItem) => {
  //     return item.productId === ticket.id && item.productType === getProductType(ticket.type);
  //   });
  //
  //   if (!existingCartItem?.tempRegistrationIds) {
  //     return [];
  //   }
  //
  //   return existingCartItem?.tempRegistrationIds?.map((id) => { return id.toString(); });
  // }, [
  //   groupRegistration,
  //   cart,
  // ]);

  const onSave = useCallback(() => {
    if (!cart) {
      return;
    }
    if (addedRegistrations.length > 0) {
      // There are more than one temp registration to add to the current ticket. Update the shopping cart.
      mutate({
        cartItem:{
          productId: ticket.id,
          productType: getProductType(ticket.type),
          quantity: 1,
          cartId: cart.id,
        },
        tempRegistrationIds: addedRegistrations.map((tempRego) => { return tempRego.id; }),
      });
    } else {
      // There is no temp registration added to the current ticket. Remove the current ticket from the shopping cart.
      const _cartItemId = getTicketCartItemId(ticket.id);
      if (_cartItemId) {
        deleteMutate({ id: _cartItemId });
      }
    }
  }, [
    addedRegistrations,
  ]);

  return (
    <UiDrawer placement={'right'} size={'lg'} isOpen={isOpen} onClose={onClose}>
      <UiDrawerOverlay />
      <UiDrawerContent bgColor={'gray.100'}>
        <UiDrawerCloseButton size={'lg'} fontWeight={'bold'} color={'primary.500'} />
        <UiDrawerHeader
          p={0}
        >
          <UiStack
            flexGrow={1}
            px={{base: 4, lg: 8}}
            // py={8}
            pt={{base: 8, lg: 8}}
            bgColor={'gray.100'}
            alignItems={'flex-start'}
          >
            <UiHStack
              // p={4}
              // borderRadius={uiStyles.buttonRadius}
              // borderWidth={'1px'}
              // borderStyle={'solid'}
              borderColor={'primary.500'}
              justifyContent={'space-around'}
              onClick={onShoppingCartOpen}
              {...uiStyles.hover}
            >
              {/*<BaseButtonIconText*/}
              {/*  Icon={UiIconShoppingCart}*/}
              {/*  // variant={'large'}*/}
              {/*  // onClick={toModify}*/}
              {/*>*/}
              {/*  Shopping cart*/}
              {/*</BaseButtonIconText>*/}
              <BaseThemeButton
                theme={'primary'}
                leftIcon={(<UiIconShoppingCart size={'xl'} color={'primary.500'}/>)}
              >
                Shopping cart
              </BaseThemeButton>
            </UiHStack>
          </UiStack>
        </UiDrawerHeader>
        <BaseDividerHorizontal height={{base: 4, lg: 8}} />
        <UiDrawerBody p={0} alignItems={'stretch'} bgColor={'gray.100'} pb={16}>
          <UiStack
            justifyContent={'flex-start'}
            alignItems={'stretch'}
            flexGrow={1}
            pb={12}
            spacing={8}
            px={{base: 4, lg: 8}}
          >
            <UiStack
              flexGrow={1}
              spacing={4}
              alignItems={'stretch'}
            // borderRadius={uiStyles.borderRadius}
            >
              {/* <UiText variant={'title'}>Ticket</UiText> */}
              <UiStack
                // enableDivider={true}
                bgColor={'#fff'}
                // minWidth={'350px'}
                borderRadius={uiStyles.borderRadius}
                spacing={0}
                py={8}
                px={{base: 4, lg: 8}}
                // borderWidth={'1px'}
                // borderColor={'gray.300'}
                // borderStyle={'solid'}
                // shadow={uiStyles.cardShadow}
              >
                <UiStack>
                  <UiText variant={'body1'} fontWeight={600}>{ticket.name}</UiText>
                  {!!ticket.description && (
                    <UiHStack>
                      <UiText variant={'body2'} color={'text.secondary'}>{ticket.description}</UiText>
                    </UiHStack>
                  )}
                </UiStack>
                <BaseDividerHorizontal height={6} />
                <UiStack spacing={4}>
                  <UiHStack>
                    <UiIconCurrencyCircleDollar size={'2xl'} color={'text.primary'} />
                    <UiText flexGrow={1}>
                      Price
                    </UiText>
                    <UiText>
                      {ticket.priceFormat}
                    </UiText>
                  </UiHStack>
                  {displayCapacities && (
                    <UiHStack>
                      <UiIconStackSimple size={'2xl'} color={'text.primary'} />
                      <UiText flexGrow={1}>
                        Tickets (available)
                      </UiText>
                      <UiText>
                        {ticket.currentStock - totalSharedStockInCart}
                      </UiText>
                    </UiHStack>
                  )}
                </UiStack>
              </UiStack>
            </UiStack>
            <BaseDividerHorizontal height={0} />
            <UiHStack
              // justifyContent={'flex-end'}
              alignItems={'stretch'}
            >
              <TicketRegistrations
                addedRegistrations={addedRegistrations}
                availableRegistrations={availableRegistrations}
                onAddRegistration={onAddRegistration}
                onRemoveRegistration={onRemoveRegistration}
              />
            </UiHStack>
          </UiStack>
        </UiDrawerBody>
        <UiDrawerFooter
          p={0}
          bgColor={'gray.100'}
          borderTopWidth={'1px'}
          borderTopStyle={'solid'}
          borderTopColor={'gray.300'}
        >
          <UiHStack justifyContent={'flex-end'} py={{base: 4, lg: 8}} px={{base: 4, lg: 8}}>
            <UiButton
              cursor={'pointer'}
              as={'button'}
              colorScheme={'primary'}
              alignItems={'center'}
              justifyContent={'center'}
              size={'lg'}
              // isDisabled={ticket.stock < 1 || values.tempRegistrationIds.length < 1}
              isLoading={isLoading || deleteMutateIsLoading || isGroupShoppingCartLoading}
              onClick={onSave}
            >
              Save
            </UiButton>
          </UiHStack>
        </UiDrawerFooter>
      </UiDrawerContent>
    </UiDrawer>
  );
};

export default TicketDrawer;
